import { createRoot } from 'react-dom/client';

/**
 * Measures the width of a rendered component to calculate how many can fit in the screen
 * @param {Function} renderFn Function that returns the component to measure
 * @param {Object} options Configuration options
 * @param {number} options.screenWidthPercent Percentage of screen width to use (0-1)
 * @returns {Promise<number>} Promise that resolves to an odd number of items that can fit
 */
export const measureComponent = (
  renderFn,
  { useableDimension = 0.8, width = true, ensureOdd = true, extraSpace = 0 } = {}
) => {
  return new Promise((resolve) => {
    // Create a temporary container for measurement
    const measureContainer = document.createElement('div');
    measureContainer.style.visibility = 'hidden';
    measureContainer.style.position = 'absolute';
    document.body.appendChild(measureContainer);

    // Create root and render using provided render function
    const root = createRoot(measureContainer);
    root.render(renderFn());

    // Wait for next frame to ensure component is rendered
    requestAnimationFrame(() => {
      // Get the measurement
      const clientDimension =
        (width
          ? measureContainer.firstChild?.scrollWidth
          : measureContainer.firstChild?.scrollHeight) || 250;

      // Cleanup
      root.unmount();
      document.body.removeChild(measureContainer);

      // Calculate number of items that can fit in screen width percentage
      const allowedScreenDimension =
        (width ? document.body.clientWidth : document.body.clientHeight) * useableDimension;
      let totalItems = Math.floor(allowedScreenDimension / (clientDimension + extraSpace));

      // Ensure odd number for center alignment
      resolve(ensureOdd ? (totalItems % 2 !== 0 ? totalItems : totalItems - 1) : totalItems);
    });
  });
};
